import React, { useState } from "react";
import { useNavigate } from "react-router";
import {
  FilterState,
  ResearchId,
} from "../../../adapters/deep-research/state/AppState.ts";
import {
  useIsResearchSending,
  useResearchState,
} from "../../../adapters/deep-research/state/hooks/stateHooks.ts";
import { useDeepResearchService } from "../../../adapters/deep-research/state/hooks/useDeepResearchService.tsx";
import { KnowledgeSource } from "../../../feature-components/KnowledgeSourceDropdown/KnowledgeSourceDropdown.tsx";
import { InsightsAgentChatBox } from "../../InsightsAgent/domain-components/ChatBox/InsightsAgentChatBox.tsx";
import { ConnectedKnowledgeSourceDropdown } from "./ConnectedKnowledgeSourceDropdown.tsx";

export const ConnectedChatBox: React.FC<{
  researchId: ResearchId;
  selectedKnowledgeSource: KnowledgeSource | undefined;
  onSourceSelect: (source: KnowledgeSource | undefined) => void;
  selectedFilters?: FilterState;
  filterDialogComponent?: React.ReactNode;
  filterChips?: React.ReactNode;
}> = (props) => {
  const appService = useDeepResearchService();
  const navigate = useNavigate();
  const research = useResearchState(props.researchId);
  const isResearchSending = useIsResearchSending(props.researchId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isProcessing =
    props.researchId === "new"
      ? isSubmitting
      : research?.status !== "completed" ||
        research.regenerating_summary ||
        isResearchSending;

  const isSubmitDisabled =
    props.researchId === "new" && !props.selectedKnowledgeSource?.value;

  const handleSubmit = async (message: string) => {
    if (props.researchId === "new") {
      setIsSubmitting(true);
      try {
        const result = await appService.startChatFirstResearch({
          message: message,
          knowledgeSourceId: props.selectedKnowledgeSource?.value,
          filters: props.selectedFilters,
        });

        if (!result.data) {
          throw new Error("Failed to create research session");
        }

        navigate(`/deep-research/${result.data.id}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      await appService.sendMessageStreaming({
        researchId: props.researchId,
        message: message,
      });
    }
  };

  return (
    <InsightsAgentChatBox
      onSubmit={handleSubmit}
      knowledgeSource={research?.knowledge_source}
      processing={isProcessing}
      submitDisabled={isSubmitDisabled}
      knowledgeSourceDropdown={
        props.researchId === "new" ? (
          <ConnectedKnowledgeSourceDropdown
            selectedSource={props.selectedKnowledgeSource}
            onSourceSelect={props.onSourceSelect}
          />
        ) : undefined
      }
      filterDialogComponent={props.filterDialogComponent}
      filterChips={props.filterChips}
    />
  );
};
